import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useBooking } from "@/context/BookingContext";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Offers", href: "#offers" },
  { label: "Packages", href: "#packages" },
  { label: "Gallery", href: "#gallery" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export default function Navbar() {
  const { openBooking, settings } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-500 ${
        scrolled
          ? "bg-[#FDFBF7]/80 backdrop-blur-xl border-b border-[#D4AF37]/25 py-3"
          : "bg-transparent py-6"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#home" className="group flex items-baseline gap-2" data-testid="nav-logo">
          <span className="font-display text-2xl sm:text-3xl leading-none text-[#2A1B1D] tracking-tight">
            Munmun
          </span>
          <span className="hidden sm:inline text-[10px] tracking-[0.28em] uppercase text-[#8E3B46] font-semibold">
            Beauty Parlour
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="relative text-sm text-[#5C4A4D] hover:text-[#8E3B46] transition-colors duration-300 after:absolute after:-bottom-1 after:left-0 after:h-px after:w-0 after:bg-[#8E3B46] after:transition-[width] after:duration-300 hover:after:w-full"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => openBooking()}
            data-testid="nav-book-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-[#8E3B46] px-6 py-2.5 text-sm font-medium text-[#FDFBF7] transition-colors duration-300 hover:bg-[#722F38]"
          >
            Book Appointment
          </button>
          <button
            className="lg:hidden p-2 text-[#2A1B1D]"
            onClick={() => setMenu((m) => !m)}
            data-testid="nav-menu-toggle"
            aria-label="Menu"
          >
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menu && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden overflow-hidden bg-[#FDFBF7]/95 backdrop-blur-xl border-t border-[#D4AF37]/20 mt-3"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMenu(false)}
                className="text-[#5C4A4D] hover:text-[#8E3B46] transition-colors"
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => {
                setMenu(false);
                openBooking();
              }}
              className="mt-2 rounded-full bg-[#8E3B46] px-6 py-3 text-sm font-medium text-[#FDFBF7]"
            >
              Book Appointment
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
